/**
 * WelcomeScreen Component
 *
 * Initial welcome screen with language selector and interview journey visualization
 */

import React from 'react';
import { Play, RefreshCw, Bot, User, MessageSquare, Check, Cpu } from 'lucide-react';

/**
 * @param {Object} props
 * @param {string} props.jobTitle - The job/position title
 * @param {Object} props.selectedLanguage - Currently selected language {code, name}
 * @param {boolean} props.showLangMenu - Whether language menu is open
 * @param {Function} props.onStart - Callback when start button is clicked
 * @param {Function} props.onLanguageToggle - Callback to toggle language menu
 * @param {Function} props.onLanguageSelect - Callback when language is selected
 * @param {Array} props.languages - Array of available languages
 * @param {number} props.questionCount - Planned total questions for the selected format
 */
export default function WelcomeScreen({
  jobTitle,
  selectedLanguage,
  showLangMenu,
  onStart,
  onLanguageToggle,
  onLanguageSelect,
  languages,
  questionCount = 20,
}) {
  return (
    <div>
      {/* Header Section */}
      <div className="flex flex-col items-center text-center space-y-6 max-w-2xl mx-auto">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center text-3xl mb-2 animate-bounce-slow">
          Hi
        </div>

        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
          Ready for your <span className="text-orange-500">dream interview?</span>
        </h1>

        <p className="text-gray-500 text-lg leading-relaxed">
          Hi! I'm your AI Interview agent, here to help you ace your next interview for the <span className="font-bold text-gray-900">{jobTitle || 'target'}</span> position. We'll go through a {questionCount}-question mock session. Once we're done, I'll provide detailed tips on your answers, highlight skills to improve, and give you an AI-powered assessment of your job fit. Shall we start?
        </p>

        <div className="flex flex-col sm:flex-row gap-4 w-full mt-4 justify-center">
          <button
            onClick={onStart}
            className="btn-orange px-8 py-4 rounded-xl text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all flex items-center justify-center gap-2 min-w-[200px]"
          >
            <Play className="w-5 h-5 fill-current" />
            Okay, let's get started
          </button>

          <button
            onClick={onLanguageToggle}
            className="bg-white border border-gray-200 text-gray-700 px-8 py-4 rounded-xl text-base font-semibold hover:bg-gray-50 transition-all flex items-center justify-center gap-2 min-w-[200px]"
          >
            <RefreshCw className="w-5 h-5" />
            Change language
          </button>

          {showLangMenu && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-10 overflow-hidden text-left max-w-xs mx-auto">
              {languages.map(lang => (
                <button
                  key={lang.code}
                  onClick={() => onLanguageSelect(lang)}
                  className="w-full text-left px-6 py-3 hover:bg-orange-50 text-gray-700 flex items-center gap-3 transition-colors"
                >
                  <span className={`w-2 h-2 rounded-full ${selectedLanguage.code === lang.code ? 'bg-orange-500' : 'bg-gray-200'}`} />
                  {lang.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* The Interview Journey - Vertical Timeline */}
      <div className="w-full max-w-3xl mx-auto mt-16">
        <div className="text-center mb-12">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">The Interview Journey</span>
        </div>

        <div className="relative">
          {/* Vertical Dashed Line */}
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-0.5 border-l-2 border-dashed border-gray-200 -ml-[1px]"></div>

          {/* Step 1: Start Interview (Left) */}
          <div className="relative z-10 flex flex-col md:flex-row items-center md:justify-between gap-6 mb-16 group">
            <div className="flex-1 w-full md:w-auto md:text-right order-2 md:order-1">
              <div className="bg-orange-50 rounded-[32px] p-8 border border-orange-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3 md:justify-end">
                  <Bot className="w-5 h-5 text-orange-500" />
                  <h3 className="font-bold text-gray-900">Start Interview</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Our AI interviewer asks the first question based on your specific job role and expertise.
                </p>
              </div>
            </div>

            <div className="order-1 md:order-2 shrink-0 w-12 h-12 rounded-full bg-white border-4 border-orange-100 flex items-center justify-center z-20 shadow-sm">
              <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
            </div>

            <div className="flex-1 w-full md:w-auto order-3 md:order-3 md:opacity-0 pointer-events-none">
              {/* Spacer for right side */}
            </div>
          </div>

          {/* Step 2: Your Answer (Right) */}
          <div className="relative z-10 flex flex-col md:flex-row items-center md:justify-between gap-6 mb-16 group">
            <div className="flex-1 w-full md:w-auto order-3 md:order-1 md:opacity-0 pointer-events-none">
              {/* Spacer for left side */}
            </div>

            <div className="order-1 md:order-2 shrink-0 w-12 h-12 rounded-full bg-white border-4 border-blue-100 flex items-center justify-center z-20 shadow-sm">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
            </div>

            <div className="flex-1 w-full md:w-auto order-2 md:order-3">
              <div className="bg-blue-50 rounded-[32px] p-8 border border-blue-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="font-bold text-gray-900">Your Answer</h3>
                  <User className="w-5 h-5 text-blue-500" />
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  You record your audio response or type it in. We analyze your tone, pace, and content.
                </p>
              </div>
            </div>
          </div>

          {/* Step 3: Follow-up Questions (Left) */}
          <div className="relative z-10 flex flex-col md:flex-row items-center md:justify-between gap-6 mb-16 group">
            <div className="flex-1 w-full md:w-auto md:text-right order-2 md:order-1">
              <div className="bg-indigo-50 rounded-[32px] p-8 border border-indigo-100 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-3 mb-3 md:justify-end">
                  <MessageSquare className="w-5 h-5 text-indigo-500" />
                  <h3 className="font-bold text-gray-900">Follow-up Questions</h3>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  The AI digs deeper, asking clarifying questions to help you fully demonstrate your skills.
                </p>
              </div>
            </div>

            <div className="order-1 md:order-2 shrink-0 w-12 h-12 rounded-full bg-white border-4 border-indigo-100 flex items-center justify-center z-20 shadow-sm">
              <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
            </div>

            <div className="flex-1 w-full md:w-auto order-3 md:order-3 md:opacity-0 pointer-events-none">
              {/* Spacer for right side */}
            </div>
          </div>

          {/* Step 4: Final Report (Center) */}
          <div className="relative z-10 flex flex-col items-center gap-6">
            <div className="shrink-0 w-12 h-12 rounded-full bg-white border-4 border-emerald-100 flex items-center justify-center z-20 shadow-sm">
              <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
            </div>

            <div className="bg-emerald-50 rounded-[32px] p-8 border border-emerald-100 max-w-sm text-center hover:shadow-md transition-shadow">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Check className="w-5 h-5 text-emerald-500" />
                <h3 className="font-bold text-gray-900">Final Report</h3>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Get a detailed breakdown of your performance with actionable tips for improvement.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom AI Mentor Card */}
      <div className="w-full max-w-3xl mx-auto bg-[#0F172A] rounded-[40px] p-8 md:p-12 text-center text-white relative overflow-hidden shadow-xl mt-16">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/5 to-transparent pointer-events-none"></div>

        <div className="relative z-10 flex flex-col items-center gap-6">
          <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center border border-white/20 shadow-inner">
            <Cpu className="w-10 h-10 text-orange-400" />
          </div>

          <h2 className="text-3xl font-bold">Your Personal AI Mentor</h2>

          <p className="text-lg text-blue-200 max-w-xl mx-auto leading-relaxed">
            I will analyze every word you say and help you become the most competitive candidate in the market.
          </p>

          <div className="flex gap-2.5 mt-2">
            <span className="w-2 h-2 bg-orange-500 rounded-full opacity-60"></span>
            <span className="w-2 h-2 bg-orange-500 rounded-full opacity-80"></span>
            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
            <span className="w-2 h-2 bg-orange-500 rounded-full opacity-80"></span>
            <span className="w-2 h-2 bg-orange-500 rounded-full opacity-60"></span>
          </div>
        </div>
      </div>
    </div>
  );
}
